/**
 * URL manipulation utilities for client-side navigation and query parameter management.
 */

/**
 * Remove query parameters from the current URL without reloading the page.
 *
 * @param params - Array of parameter names to remove, or 'all' to remove all params
 * @param pathname - Optional pathname to navigate to (defaults to current pathname)
 *
 * @example
 * ```ts
 * // Remove specific params
 * removeQueryParams(['connected', 'checkout'], '/dashboard');
 *
 * // Remove all params
 * removeQueryParams('all');
 *
 * // Remove params from current path
 * removeQueryParams(['error', 'message']);
 * ```
 */
export function removeQueryParams(params: string[] | 'all', pathname?: string): void {
  if (typeof window === 'undefined') return;

  const url = new URL(window.location.href);

  if (params === 'all') {
    url.search = '';
  } else {
    params.forEach((param) => url.searchParams.delete(param));
  }

  const targetPath = pathname || url.pathname;
  window.history.replaceState({}, '', targetPath + url.search);
}

/**
 * Remove a single query parameter from the current URL.
 *
 * @param param - Parameter name to remove
 * @param pathname - Optional pathname to navigate to (defaults to current pathname)
 *
 * @example
 * ```ts
 * removeQueryParam('auth'); // e.g. the post-OAuth `auth=pending` marker
 * removeQueryParam('error', '/dashboard');
 * ```
 */
export function removeQueryParam(param: string, pathname?: string): void {
  removeQueryParams([param], pathname);
}
